import styled, { css } from 'styled-components';
import Screen from 'src/styles/Themes/Screen';
import { cssVars } from '@/styles/Themes/cssVars';
import { Rating } from '@/entities/Activity/ui/Rating';
import Palette from '@/styles/Themes/Palette';
import Text from '@/styles/Themes/Text';
import { useEffect } from 'react';
import { numberWithCommaFormatter } from '@/common/app/utils/numberUtils';
import { useShopperApprovedStore } from './store';

export const ShopperApprovedStickyBar = () => {
    const { data, getSAData, recommendedRate } = useShopperApprovedStore();

    useEffect(() => {
        getSAData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Wrapper>
            <picture>
                <source
                    media="(min-width: 992px)"
                    srcSet="/image/shopper_approved_desktop.png"
                    width="725"
                    height="113"
                />
                <Image
                    src="/image/shopper_approved_mobile.png"
                    alt="ShopperApproved"
                    width="696"
                    height="189"
                    decoding="async"
                />
            </picture>
            <CommonBlock>
                <Block>
                    <Info marginRight>Rated &apos;Excellent&apos;</Info>
                    <Rating rating={{ full: 5, half: 0 }} />
                    <Rate isLoading={!data}>
                        {data?.average_rating
                            ? numberWithCommaFormatter(data.average_rating)
                            : '4.8'}
                    </Rate>
                </Block>
                <Block>
                    <Reviews
                        href="https://www.shopperapproved.com/reviews/TripShock.com"
                        target="_blank"
                        isLoading={!data}
                    >
                        {`${
                            data?.total_reviews
                                ? numberWithCommaFormatter(data.total_reviews)
                                : '35,073'
                        } Reviews`}
                    </Reviews>
                    <Percent>{recommendedRate || '96%'}</Percent>
                    <Info>Recommend Us</Info>
                </Block>
            </CommonBlock>
        </Wrapper>
    );
};

const Wrapper = styled.div`
    width: 100%;
    height: fit-content;
    padding: 10px 4px 12px;
    position: relative;
    z-index: -1;

    ${Screen.desktop} {
        padding: 8px 30px;
    }

    border: 1px solid ${cssVars.bg_sand_dark3};
    border-radius: 0 0 16px 16px;
    background-color: ${cssVars.bg_lightSand_dark2};
    display: flex;
    justify-content: center;
    align-items: center;
`;

const CommonBlock = styled.div`
    display: block;

    ${Screen.desktop} {
        display: flex;
    }
`;

const Block = styled.div`
    display: flex;
    align-items: center;

    ${Screen.desktop} {
        margin-top: 2px;
    }

    svg {
        scale: 1.4;
        width: 14px;
        height: 12px;
        margin-right: 2px;

        ${Screen.ipad} {
            scale: 1.6;
            width: 17px;
            height: 12px;
            margin-right: 2px;
        }
    }
`;

const Image = styled.img`
    height: 24px;
    margin-right: 8px;
    width: 88px;

    ${Screen.ipad} {
        margin-right: 20px;
        width: 154px;
    }
`;

const Info = styled.span<{ marginRight?: boolean }>`
    ${Text.Full.large.regular};
    color: ${cssVars.fg_gray2_gray3};
    text-wrap: nowrap;

    ${({ marginRight }) =>
        marginRight &&
        css`
            margin-right: 12px;

            ${Screen.ipad} {
                margin-right: 20px;
            }
        `}
`;

const Rate = styled.span<{ isLoading?: boolean }>`
    ${Text.Full.large.semibold};
    color: ${cssVars.fg_gray1_white};
    margin: 0;

    ${Screen.ipad} {
        margin: 0 20px 0 4px;
    }

    @keyframes appearanceText {
        from {
            opacity: 1%;
            filter: blur(1px);
        }
        to {
            filter: blur(4px);
            opacity: 100%;
        }
    }

    ${({ isLoading }) =>
        isLoading &&
        css`
            animation-duration: 1s;
            animation-name: appearanceText;
            animation-iteration-count: infinite;
            animation-direction: alternate;
        `};
`;

const Reviews = styled.a<{ isLoading?: boolean }>`
    ${Text.Full.medium.semibold};
    color: ${Palette.state.info};
    text-decoration: underline;

    padding-right: 4px;
    border-right: 1px solid ${cssVars.fg_gray3_dark1};

    ${Screen.ipad} {
        padding-right: 20px;
    }

    ${Screen.desktop} {
        height: 24px;
        padding-left: 20px;
        padding-right: 20px;
        border-left: 1px solid ${cssVars.fg_gray3_dark1};
    }

    @keyframes appearanceText {
        from {
            opacity: 1%;
            filter: blur(1px);
        }
        to {
            filter: blur(4px);
            opacity: 100%;
        }
    }

    ${({ isLoading }) =>
        isLoading &&
        css`
            animation-duration: 1s;
            animation-name: appearanceText;
            animation-iteration-count: infinite;
            animation-direction: alternate;
        `};
`;

const Percent = styled.div`
    ${Text.Full.large.semibold};
    color: ${Palette.green.normal};
    margin: 0 4px 0 4px;

    ${Screen.ipad} {
        margin: 0 4px 0 20px;
    }
`;
